import { Controller } from "@hotwired/stimulus"

import { entryNetMinutes } from "../utils/timesheet_net_time"

// Drives the "Schnellerfassung" batch form: add/remove entry rows per day and
// toggle a whole day in/out (week mode). Each row's fields are named
// entries[<index>][...]; clones get a fresh unique index. Inclusion is carried
// by a per-row hidden `include` input (the day checkbox has no name and only
// drives these hiddens), so the server reads inclusion per entry.
export default class extends Controller {
  static targets = ["group", "rows", "entry"]

  // Einheit und Dezimaltrennzeichen kommen aus den Übersetzungen, nicht aus dem Code:
  // die Zusammenfassung steht neben serverseitig gesetzten Stunden und muss dieselbe
  // Schreibweise treffen. Die Vorgaben halten die Zeile lesbar, falls die Attribute
  // einmal fehlen.
  static values = {
    hoursUnit: { type: String, default: "h" },
    decimalSeparator: { type: String, default: "," },
  }

  connect() {
    this.counter = this.maxIndex() + 1
    // The hours each row books, kept next to its times. Without it the reader
    // subtracts the Pause themselves, which is how a 11:00-11:30 booking with a
    // pre-filled 00:30 Pause reached the list as 0,00 h before anyone noticed.
    // break_rule_controller writes the Pause on the same events, so this listens
    // for its change too rather than only for typing.
    this._renderNet = () => this.renderNetTimes()
    this.element.addEventListener("input", this._renderNet)
    this.element.addEventListener("change", this._renderNet)
    this.renderNetTimes()
  }

  disconnect() {
    this.element.removeEventListener("input", this._renderNet)
    this.element.removeEventListener("change", this._renderNet)
  }

  renderNetTimes() {
    this.entryTargets.forEach((entry) => {
      const slot = entry.querySelector("[data-quick-entry-net]")
      if (!slot) return

      const value = (field) => entry.querySelector(`input[name$='[${field}]']`)?.value || ""
      const from = value("time_from")
      const to = value("time_to")
      const minutes = entryNetMinutes({
        time_from: from,
        time_to: to,
        time_break: value("time_break"),
      })

      slot.textContent = from && to ? this.formatHours(minutes) : ""
      // The whole summary or none of it: a window left at its server-rendered
      // value beside a live Pause and a live total reads as nonsense — 06:00-15:30
      // next to 0,50 h.
      this.writeSlot(entry, "from", from)
      this.writeSlot(entry, "to", to)
      this.writeBreakSlot(entry, value("time_break"))
    })
  }

  writeSlot(entry, name, text) {
    const slot = entry.querySelector(`[data-quick-entry-${name}]`)
    if (slot) slot.textContent = text
  }

  // "· Pause 00:30" is shown only while there IS one. The label and its separator
  // are rendered either way so that a Pause the break rule adds later has
  // somewhere to land — without them the summary would keep announcing a window
  // and a total that no longer subtract to each other.
  writeBreakSlot(entry, text) {
    this.writeSlot(entry, "break", text)
    ;["break-separator", "break-label"].forEach((name) => {
      entry.querySelector(`[data-quick-entry-${name}]`)?.classList.toggle("hidden", !text)
    })
  }

  // Matching how the hours read back out of the list.
  formatHours(minutes) {
    const hours = (minutes / 60).toFixed(2).replace(".", this.decimalSeparatorValue)
    return `${hours} ${this.hoursUnitValue}`
  }

  add(event) {
    const group = event.currentTarget.closest("[data-quick-entries-target='group']")
    if (!group) return

    const rows = group.querySelector("[data-quick-entries-target='rows']")
    const first = rows?.querySelector("[data-quick-entries-target='entry']")
    if (!rows || !first) return

    const index = this.counter++
    const clone = first.cloneNode(true)

    this.rewrite(clone, index)
    this.clearSite(clone)
    this.forceIncluded(clone)
    this.showRemove(clone)
    this.resetSlimSelects(clone)

    rows.appendChild(clone)
  }

  remove(event) {
    event.currentTarget.closest("[data-quick-entries-target='entry']")?.remove()
  }

  toggleDay(event) {
    const group = event.currentTarget.closest("[data-quick-entries-target='group']")
    if (!group) return

    const on = event.currentTarget.checked
    group.querySelectorAll("input[name$='[include]']").forEach((hidden) => {
      hidden.value = on ? "1" : "0"
    })

    const rows = group.querySelector("[data-quick-entries-target='rows']")
    if (rows) rows.classList.toggle("opacity-50", !on)
  }

  // --- helpers ---

  maxIndex() {
    let max = -1
    this.element.querySelectorAll("[name^='entries[']").forEach((input) => {
      const match = input.name.match(/^entries\[(\d+)\]/)
      if (match) max = Math.max(max, parseInt(match[1], 10))
    })
    return max
  }

  rewrite(clone, index) {
    clone.querySelectorAll("input, select, textarea").forEach((input) => {
      if (input.name) input.name = input.name.replace(/^entries\[\d+\]/, `entries[${index}]`)
      if (input.id) input.id = input.id.replace(/^entry_\d+_/, `entry_${index}_`)
    })

    clone.querySelectorAll("label[for]").forEach((label) => {
      const target = label.getAttribute("for")
      if (target && /^entry_\d+_/.test(target)) {
        label.setAttribute("for", target.replace(/^entry_\d+_/, `entry_${index}_`))
      }
    })

    const wrapper = clone.querySelector("[id$='_task_ids_wrapper']")
    if (wrapper) wrapper.id = `entry_${index}_task_ids_wrapper`

    const taskSelect = clone.querySelector("select[id$='_task_ids']")
    if (taskSelect) taskSelect.id = `entry_${index}_task_ids`

    const siteSelect = clone.querySelector("select[data-model='configurable_timesheet']")
    if (siteSelect) {
      siteSelect.dataset.wrapperId = `entry_${index}_task_ids_wrapper`
      siteSelect.dataset.dropdownId = `entry_${index}_task_ids`
    }
  }

  clearSite(clone) {
    const site = clone.querySelector("select[data-model='configurable_timesheet']")
    if (site) site.selectedIndex = 0

    const wrapper = clone.querySelector("[id$='_task_ids_wrapper']")
    if (wrapper) wrapper.classList.add("hidden")

    const taskSelect = clone.querySelector("select[id$='_task_ids']")
    if (taskSelect) Array.from(taskSelect.options).forEach((option) => (option.selected = false))
  }

  forceIncluded(clone) {
    const hidden = clone.querySelector("input[name$='[include]']")
    if (hidden) hidden.value = "1"
  }

  showRemove(clone) {
    clone.querySelector("[data-quick-entries-target='remove']")?.classList.remove("hidden")
  }

  resetSlimSelects(clone) {
    clone.querySelectorAll(".ss-main").forEach((wrapper) => wrapper.remove())
    clone.querySelectorAll("select").forEach((select) => {
      select.style.cssText = ""
      select.removeAttribute("tabindex")
      select.removeAttribute("aria-hidden")
      delete select.dataset.id
      select.classList.remove("ss-hide")
    })
  }
}
